import { formatLocalDate } from '../utils/localDateTime';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { Organization } from '@types';
import { functions } from '../../firebaseConfig';
import { serviceRequestDownloadUrl } from '../common/serviceRequestFiles';
import { sortNewestDeliveries } from '../common/sortNewestDeliveries';
import { ReportNavigationContext } from '../common/reportNavigation';

type DeliveryFile = { name: string; path: string; contentType: string; size: number };
type DeliveryReport = { id: string; title: string; type?: string; consultantReport?: boolean };
type LibraryReport = { id: string; title: string; type: string; savedAt?: string };
type DeliveredRequest = {
    id: string;
    requestReference: string;
    serviceName: string;
    deliveredAt?: string;
    deliveryNotes?: string;
    deliveryClassification?: string;
    deliveryFiles?: DeliveryFile[];
    deliveryReports?: DeliveryReport[];
    status: string;
};

function formatDate(value?: string) {
    return value ? formatLocalDate(value) : 'Date unavailable';
}

function formatSize(bytes: number) {
    if (bytes < 1024 * 1024)
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const IntelligenceLibraryPage: React.FC<{ organization: Organization; onOpenReport: (reportId: string) => void }> = ({ organization, onOpenReport }) => {
    const [requests, setRequests] = useState<DeliveredRequest[]>([]);
    const [savedReports, setSavedReports] = useState<LibraryReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [opening, setOpening] = useState('');
    const [error, setError] = useState('');
    const openDeliveredReport = useContext(ReportNavigationContext);
    const callable = useMemo(() => httpsCallable(functions, 'manageConsultantReports'), []);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setRequests([]);
        setSavedReports([]);
        void callable({ action: 'library' }).then(result => {
            if (!active)
                return;
            const items = ((result.data as any).requests || []) as DeliveredRequest[];
            setRequests(sortNewestDeliveries(items.filter(item => ['delivered', 'invoiced', 'completed'].includes(item.status) && (item.deliveryFiles?.length || item.deliveryReports?.length))));
            setSavedReports((result.data as any).savedReports || []);
        }).catch(failure => {
            if (active)
                setError(failure.message || 'Intelligence products failed loading.');
        }).finally(() => {
            if (active)
                setLoading(false);
        });
        return () => { active = false; };
    }, [callable, organization.id]);

    const openFile = async (requestId: string, file: DeliveryFile) => {
        const popup = window.open('about:blank', '_blank');
        if (popup)
            popup.opener = null;
        setOpening(file.path);
        setError('');
        try {
            const url = await serviceRequestDownloadUrl(organization.id, requestId, file);
            if (popup)
                popup.location.href = url;
            else
                window.location.assign(url);
        } catch (failure: any) {
            popup?.close();
            setError(failure.message || 'Completed product could not be opened.');
        } finally {
            setOpening('');
        }
    };

    if (loading)
        return <div className="py-24 text-center text-sm font-semibold text-slate-400">Loading intelligence products...</div>;

    return <div className="mx-auto max-w-6xl space-y-6">
        <div>
            <h1 className="text-3xl font-black text-slate-900">Intelligence Library</h1>
            <p className="mt-1 text-sm text-slate-500">Saved reports and completed intelligence products for your organization.</p>
        </div>
        {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}
        {savedReports.length > 0 && <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <h2 className="border-b border-slate-200 bg-slate-50 px-6 py-4 text-lg font-extrabold text-slate-900">Saved Intelligence Reports</h2>
            <div className="divide-y divide-slate-100">{savedReports.map(report => <div key={report.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
                <div><p className="font-bold text-slate-800">{report.title}</p><p className="mt-1 text-xs text-slate-400">Saved {formatDate(report.savedAt)}</p></div>
                <button type="button" onClick={() => onOpenReport(`saved-intel:${report.id}`)} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">View Report</button>
            </div>)}</div>
        </section>}
        {!requests.length && !savedReports.length ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
            <svg className="mx-auto h-12 w-12 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/></svg>
            <p className="mt-4 font-bold text-slate-700">No intelligence reports yet.</p>
            <p className="mt-1 text-sm text-slate-400">Saved reports and delivered service work will appear here.</p>
        </div> : requests.length > 0 ? <div className="space-y-4">{requests.map(request => <section key={request.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div><p className="text-xs font-bold uppercase tracking-wider text-blue-600">{request.requestReference}</p><h2 className="mt-1 text-lg font-extrabold text-slate-900">{request.serviceName}</h2></div>
                    <div className="text-right"><p className="text-sm font-semibold text-slate-600">Delivered {formatDate(request.deliveredAt)}</p><p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-400">{request.deliveryClassification || 'Restricted'}</p></div>
                </div>
                {request.deliveryNotes && <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-600">{request.deliveryNotes}</p>}
            </div>
            <div className="divide-y divide-slate-100">{(request.deliveryReports || []).map(report => <div key={report.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
                <div><p className="font-bold text-slate-800">{report.title}</p><p className="mt-1 text-xs uppercase tracking-wide text-slate-400">Saved report</p></div>
                <button type="button" onClick={() => report.consultantReport || report.id.startsWith('consultant-')
                    ? (openDeliveredReport || onOpenReport)(report.id) : onOpenReport(report.id)} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">View Report</button>
            </div>)}{(request.deliveryFiles || []).map(file => <div key={file.path} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
                <div className="flex min-w-0 items-center gap-3"><div className="rounded-xl bg-blue-50 p-2.5 text-blue-600"><svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/></svg></div><div className="min-w-0"><p className="truncate font-bold text-slate-800">{file.name}</p><p className="mt-0.5 text-xs uppercase tracking-wide text-slate-400">{formatSize(file.size)}</p></div></div>
                <button type="button" disabled={opening === file.path} onClick={() => void openFile(request.id, file)} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">{opening === file.path ? 'Opening...' : 'Open Product'}</button>
            </div>)}</div>
        </section>)}</div> : null}
    </div>;
};

export default IntelligenceLibraryPage;
